import React, { useState, useEffect } from 'react';
import { CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, XAxis, YAxis } from 'recharts';
import { CheckCircle, XCircle, Palette, BarChart3, Sparkles, Play, Image, Maximize2, X, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { Toaster } from 'react-hot-toast';
import { useParams, useNavigate } from 'react-router-dom';

// Custom formatter to display dollar values with commas
const formatDollar = (value) => `$${value.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;

// Format percentage values
const formatPercent = (value) => {
  if (typeof value !== 'number' || isNaN(value)) {
    return 'N/A';
  }
  return `${value > 0 ? '+' : ''}${value.toFixed(1)}%`;
};

const SharedCreativeReport = () => {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [visualSubTab, setVisualSubTab] = useState('adTypes');
  const [fullscreenMedia, setFullscreenMedia] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'roas', direction: 'desc' });
  const [topPerformersSortConfig, setTopPerformersSortConfig] = useState({ key: 'roas', direction: 'desc' });
  const [sharedReportInfo, setSharedReportInfo] = useState(null);
  const [aiInsights, setAiInsights] = useState({
    keyActionItems: '',
    topPerformerInsights: '',
    keyRecommendations: ''
  });

  // Handle Escape key to close fullscreen modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && fullscreenMedia) {
        setFullscreenMedia(null);
      }
    };

    if (fullscreenMedia) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [fullscreenMedia]);

  // Fetch shared report data
  useEffect(() => {
    const fetchSharedReport = async () => {
      try {
        setIsLoading(true);
        const response = await axios.get(`/api/reports/shared/${reportId}`);
        
        if (response.data.success) {
          setReportData(response.data.report);
          setSharedReportInfo({
            accountName: response.data.report.accountName || 'Account',
            dateRange: response.data.report.dateRange || 'Unknown',
            sharedAt: response.data.report.sharedAt,
            accessCount: response.data.report.accessCount || 0
          });
          
          // Set AI insights if available
          if (response.data.report.aiInsights) {
            setAiInsights(response.data.report.aiInsights);
          }
        } else {
          setError('Report not found or access denied');
        }
      } catch (err) {
        console.error('Error fetching shared report:', err);
        setError('Failed to load report. Please check the link or contact support.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchSharedReport();
  }, [reportId]);

  // Media preview component with fullscreen support
  const MediaPreview = ({ url, title, isVideo = false }) => {
    const handleClick = () => {
      console.log('MediaPreview clicked:', { url, title, isVideo });
      console.log('Setting fullscreen media:', { url, title, type: isVideo ? 'video' : 'image' });
      setFullscreenMedia({ url, title, type: isVideo ? 'video' : 'image' });
    };

    return (
      <div className="relative group">
        <div 
          className="w-24 h-20 sm:w-32 sm:h-24 lg:w-40 lg:h-32 bg-gray-200 rounded-lg overflow-hidden flex items-center justify-center cursor-pointer hover:scale-105 transition-transform"
          onClick={handleClick}
        >
          {isVideo ? (
            <video 
              src={url} 
              className="w-full h-full object-cover"
              controls
              preload="metadata"
              onError={(e) => {
                console.error('Video failed to load:', url);
                e.target.style.display = 'none';
              }}
            />
          ) : (
            <img 
              src={url} 
              alt={title}
              className="w-full h-full object-cover"
              onError={(e) => {
                console.error('Image failed to load:', url);
                e.target.style.display = 'none';
              }}
            />
          )}
          <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            {isVideo ? <Play className="h-8 w-8 text-white" /> : <Maximize2 className="h-8 w-8 text-white" />}
          </div>
        </div>
      </div>
    );
  };

  // Render feature examples with images/videos
  const renderFeatureExamples = (featureName, item = null, isPositive = true) => {
    // If we have an item with asset URLs, use those
    if (item && item.assetUrls && item.assetUrls.length > 0) {
      const assetUrls = Array.isArray(item.assetUrls) ? item.assetUrls : [item.assetUrls];
      const mediaTypes = item.mediaTypes || [];
      
      // Filter out empty or null URLs - more robust filtering
      const validAssetUrls = assetUrls.filter(url => {
        if (!url) return false;
        if (typeof url !== 'string') return false;
        if (url.trim() === '') return false;
        if (url === 'null' || url === 'undefined') return false;
        return true;
      });
      
      console.log('URL filtering:', {
        originalUrls: assetUrls,
        validUrls: validAssetUrls,
        featureName: featureName,
        originalLength: assetUrls.length,
        validLength: validAssetUrls.length
      });
      
      if (validAssetUrls.length === 0) {
        return null; // No valid URLs to display
      }
      
      return (
        <div className="flex flex-wrap gap-2 mt-2">
          {validAssetUrls.slice(0, 2).map((url, index) => {
            // Better media type detection based on URL path and extension (prioritize URL over database)
            const urlLower = url.toLowerCase();
            const isVideoFromUrl = urlLower.includes('.mp4') || urlLower.includes('.mov') || urlLower.includes('.avi') || urlLower.includes('.webm') || urlLower.includes('/video/');
            const isImageFromUrl = urlLower.includes('.jpg') || urlLower.includes('.jpeg') || urlLower.includes('.png') || urlLower.includes('.gif') || urlLower.includes('.webp') || urlLower.includes('/image/');
            
            // Prioritize URL-based detection over database media type for accuracy
            let shouldRenderAsVideo = false;
            if (isVideoFromUrl) {
              shouldRenderAsVideo = true;
            } else if (isImageFromUrl) {
              shouldRenderAsVideo = false;
            } else {
              // Fallback to database media type only if URL doesn't have clear extension
              const dbMediaType = mediaTypes[index];
              shouldRenderAsVideo = dbMediaType === 'Video';
            }
            
            console.log('Media type detection:', {
              url,
              index,
              isVideoFromUrl,
              isImageFromUrl,
              dbMediaType: mediaTypes[index],
              shouldRenderAsVideo,
              featureName
            });
            
            return (
              <div key={index} className="relative">
                <MediaPreview 
                  url={url} 
                  title={`${featureName} example ${index + 1}`}
                  isVideo={shouldRenderAsVideo}
                />
                <div className="absolute -bottom-8 left-0 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                  {featureName} {isPositive ? '(Top Performer)' : '(Underperformer)'}
                </div>
              </div>
            );
          })}
        </div>
      );
    }

    // If we have a single asset URL (for top content)
    if (item && item.assetUrl && typeof item.assetUrl === 'string' && item.assetUrl.trim() !== '' && item.assetUrl !== 'null' && item.assetUrl !== 'undefined') {
      const urlLower = item.assetUrl.toLowerCase();
      const isVideoFromUrl = urlLower.includes('.mp4') || urlLower.includes('.mov') || urlLower.includes('.avi') || urlLower.includes('.webm') || urlLower.includes('/video/');
      const isImageFromUrl = urlLower.includes('.jpg') || urlLower.includes('.jpeg') || urlLower.includes('.png') || urlLower.includes('.gif') || urlLower.includes('.webp') || urlLower.includes('/image/');
      
      // Prioritize URL-based detection over database media type for accuracy
      let shouldRenderAsVideo = false;
      if (isVideoFromUrl) {
        shouldRenderAsVideo = true;
      } else if (isImageFromUrl) {
        shouldRenderAsVideo = false;
      } else {
        // Fallback to database media type only if URL doesn't have clear extension
        shouldRenderAsVideo = item.mediaType === 'Video';
      }
      
      return (
        <div className="flex flex-wrap gap-2 mt-2">
          <div className="relative">
            <MediaPreview 
              url={item.assetUrl} 
              title={item.name}
              isVideo={shouldRenderAsVideo}
            />
            <div className="absolute -bottom-8 left-0 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
              {item.name}
            </div>
          </div>
        </div>
      );
    }

    // No media available
    return null;
  };

  // Dashboard info state
  const [dashboardInfo] = useState({
    title: "Creative Performance Analysis",
    lastUpdated: new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }),
    totalSpend: 0,
    averageROAS: 0,
    analyzedCreatives: 0
  });



  // Sorting function
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Sorting function for top performers table
  const handleTopPerformersSort = (key) => {
    let direction = 'asc';
    if (topPerformersSortConfig.key === key && topPerformersSortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setTopPerformersSortConfig({ key, direction });
  };

  // Helper function to get sorted top performers data
  const getSortedTopPerformers = () => {
    if (!reportData || !reportData.topContent) return [];
    
    let data = [...reportData.topContent];
    
    // Apply sorting if configured
    if (topPerformersSortConfig.key) {
      data.sort((a, b) => {
        let aValue = a[topPerformersSortConfig.key];
        let bValue = b[topPerformersSortConfig.key];
        
        // Handle numeric values
        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return topPerformersSortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
        }
        
        // Handle string values
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return topPerformersSortConfig.direction === 'asc' 
            ? aValue.localeCompare(bValue) 
            : bValue.localeCompare(aValue);
        }
        
        return 0;
      });
    }
    
    return data;
  };

  // Helper function to get the right visual data based on active subtab
  const getVisualData = () => {
    if (!reportData) return [];
    
    console.log('getVisualData called:', { visualSubTab, reportData });
    
    let data = [];
    switch(visualSubTab) {
      case 'adTypes': 
        console.log('Returning adTypes:', reportData.adTypes);
        data = reportData.adTypes || [];
        break;
      case 'features': 
        console.log('Returning all features for detailed analysis');
        // For detailed analysis, return all features (top 50)
        data = reportData.features || [];
        break;
      default: 
        console.log('Returning default adTypes:', reportData.adTypes);
        data = reportData.adTypes || [];
        break;
    }
    
    // Apply sorting if configured
    if (sortConfig.key) {
      data = [...data].sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];
        
        // Handle numeric values
        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
        }
        
        // Handle string values
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return sortConfig.direction === 'asc' 
            ? aValue.localeCompare(bValue) 
            : bValue.localeCompare(aValue);
        }
        
        return 0;
      });
    }
    
    // Limit to 50 items for detailed analysis
    return data.slice(0, 50);
  };

  // Get color for impact
  const getColorForImpact = (impact) => {
    if (impact > 0) return '#10b981'; // green
    if (impact < 0) return '#ef4444'; // red
    return '#6b7280'; // gray
  };

  // Format date range from the shared report data
  const getFormattedDateRange = () => {
    if (!sharedReportInfo?.dateRange) return 'Unknown date range';
    
    // If it's a preset range like "Last 30 days", calculate actual dates
    if (sharedReportInfo.dateRange.includes('Last') && sharedReportInfo.dateRange.includes('days')) {
      const daysMatch = sharedReportInfo.dateRange.match(/Last (\d+) days/);
      if (daysMatch) {
        const days = parseInt(daysMatch[1]);
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(endDate.getDate() - days);
        
        return `${startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
      }
    }
    
    // If it's already a custom date range, return as is
    return sharedReportInfo.dateRange;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center max-w-md w-full px-6">
          <div className="mb-6">
            <Sparkles className="h-12 w-12 text-purple-400 mx-auto mb-4 animate-pulse" />
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Loading Creative Report</h2>
            <p className="text-gray-600">Loading your shared creative performance data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-6">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            <p className="font-medium">Error Loading Report</p>
            <p className="text-sm">{error}</p>
          </div>
          <button
            onClick={() => navigate('/')}
            className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700 transition-colors"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  if (!reportData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 text-lg">No report data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-gray-50 p-6 min-h-screen">
      {/* Shared Report Header */}
      <div className="mb-6">
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/')}
              className="text-gray-500 hover:text-gray-700 transition-colors p-2 rounded-lg hover:bg-gray-100"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div className="text-left">
              <h1 className="text-3xl font-bold text-gray-800 flex items-center">
                <Sparkles className="h-8 w-8 text-purple-400 mr-3" />
                ShowStop AI - {'Demo Account'}
              </h1>
              <p className="text-gray-600">
                {getFormattedDateRange()} • Last Updated: {dashboardInfo.lastUpdated}
                {sharedReportInfo?.accessCount && (
                  <span className="ml-2">• Viewed {sharedReportInfo.accessCount} times</span>
                )}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Top KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
        <div className="text-center p-3 sm:p-4 bg-white rounded-lg shadow">
          <p className="text-gray-500 text-xs sm:text-sm">Total Spend</p>
          <p className="text-2xl sm:text-3xl font-bold text-blue-700">{formatDollar(reportData.metrics?.totalSpend || 0)}</p>
        </div>
        <div className="text-center p-3 sm:p-4 bg-white rounded-lg shadow">
          <p className="text-gray-500 text-xs sm:text-sm">Average ROAS</p>
          <p className="text-2xl sm:text-3xl font-bold text-green-700">{(reportData.metrics?.avgROAS || 0).toFixed(2)}x</p>
        </div>
        <div className="text-center p-3 sm:p-4 bg-white rounded-lg shadow">
          <p className="text-gray-500 text-xs sm:text-sm">Analyzed Creatives</p>
          <p className="text-2xl sm:text-3xl font-bold text-purple-700">{(reportData.metrics?.totalAds || 0).toLocaleString()}</p>
        </div>
        <div className="text-center p-3 sm:p-4 bg-white rounded-lg shadow">
          <p className="text-gray-500 text-xs sm:text-sm">Best Element Impact</p>
          <p className="text-2xl sm:text-3xl font-bold text-green-700">
            {reportData.bestAdTypes?.length > 0 ? formatPercent(reportData.bestAdTypes[0].impact) : 
             reportData.bestFeatures?.length > 0 ? formatPercent(reportData.bestFeatures[0].impact) : '+0%'}
          </p>
          <p className="text-xs text-gray-600">
            {reportData.bestAdTypes?.length > 0 ? reportData.bestAdTypes[0].name : 
             reportData.bestFeatures?.length > 0 ? reportData.bestFeatures[0].name : 'No data'}
          </p>
        </div>
      </div>

      {/* Main Tabs */}
      <div className="flex flex-wrap border-b mb-6">
        <button
          className={`py-3 px-4 sm:px-6 font-medium transition-colors border-b-2 ${activeTab === 'overview' ? 'text-blue-600 border-blue-600' : 'text-gray-500 border-transparent hover:text-gray-700'}`}
          onClick={() => setActiveTab('overview')}>
          Executive Summary
        </button>
        <button
          className={`py-3 px-4 sm:px-6 font-medium transition-colors border-b-2 ${activeTab === 'topPerformers' ? 'text-blue-600 border-blue-600' : 'text-gray-500 border-transparent hover:text-gray-700'}`}
          onClick={() => setActiveTab('topPerformers')}>
          Top Performers
        </button>
        <button
          className={`py-3 px-4 sm:px-6 font-medium transition-colors border-b-2 ${activeTab === 'visual' ? 'text-blue-600 border-blue-600' : 'text-gray-500 border-transparent hover:text-gray-700'}`}
          onClick={() => setActiveTab('visual')}>
          Creative Analysis
        </button>
        <button
          className={`py-3 px-4 sm:px-6 font-medium transition-colors border-b-2 ${activeTab === 'insights' ? 'text-blue-600 border-blue-600' : 'text-gray-500 border-transparent hover:text-gray-700'}`}
          onClick={() => setActiveTab('insights')}>
          <Sparkles className="h-4 w-4 inline mr-1" />
          Creative Insights
        </button>
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-lg shadow p-6 mb-6 flex-grow">
        {/* Executive Summary Tab */}
        {activeTab === 'overview' && (
          <div>
            <h2 className="text-xl font-bold mb-4 text-gray-800">Executive Summary</h2>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-8 mb-6">
              <div className="bg-green-50 p-3 sm:p-5 rounded-lg">
                <div className="flex items-center mb-4">
                  <CheckCircle className="h-6 w-6 text-green-600 mr-2" />
                  <h3 className="text-lg font-bold text-green-800">TOP PERFORMING AD TYPES</h3>
                </div>
                <div className="space-y-3">
                  {reportData.bestAdTypes?.map((item, index) => (
                    <div key={index} className="flex bg-white p-3 rounded-lg shadow-sm">
                      <div className="flex-1 flex flex-col justify-center px-4">
                        <div className="flex justify-between items-center">
                          <div className="flex items-center">
                            <span className="font-medium">{item.name}</span>
                            {item.name.toLowerCase().includes('video') && (
                              <Play className="h-4 w-4 text-blue-600 ml-2" />
                            )}
                            {item.name.toLowerCase().includes('image') && (
                              <Image className="h-4 w-4 text-purple-600 ml-2" />
                            )}
                          </div>
                          <span className="text-green-600 font-bold">{formatPercent(item.impact)}</span>
                        </div>
                        <div className="mt-1 text-sm text-gray-500 flex justify-between">
                          <span>ROAS: {item.roas.toFixed(2)}x</span>
                          <span>Spend: {formatDollar(item.spend)}</span>
                        </div>
                        {renderFeatureExamples(item.name, item, true)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-red-50 p-3 sm:p-5 rounded-lg">
                <div className="flex items-center mb-4">
                  <XCircle className="h-6 w-6 text-red-600 mr-2" />
                  <h3 className="text-lg font-bold text-red-800">UNDERPERFORMING AD TYPES</h3>
                </div>
                <div className="space-y-3">
                  {reportData.worstAdTypes?.map((item, index) => (
                    <div key={index} className="flex bg-white p-3 rounded-lg shadow-sm">
                      <div className="flex-1 flex flex-col justify-center px-4">
                        <div className="flex justify-between items-center">
                          <div className="flex items-center">
                            <span className="font-medium">{item.name}</span>
                            {item.name.toLowerCase().includes('video') && (
                              <Play className="h-4 w-4 text-blue-600 ml-2" />
                            )}
                            {item.name.toLowerCase().includes('image') && (
                              <Image className="h-4 w-4 text-purple-600 ml-2" />
                            )}
                          </div>
                          <span className="text-red-600 font-bold">{formatPercent(item.impact)}</span>
                        </div>
                        <div className="mt-1 text-sm text-gray-500 flex justify-between">
                          <span>ROAS: {item.roas.toFixed(2)}x</span>
                          <span>Spend: {formatDollar(item.spend)}</span>
                        </div>
                        {renderFeatureExamples(item.name, item, false)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-8 mb-6">
              <div className="bg-green-50 p-3 sm:p-5 rounded-lg">
                <div className="flex items-center mb-4">
                  <CheckCircle className="h-6 w-6 text-green-600 mr-2" />
                  <h3 className="text-lg font-bold text-green-800">TOP PERFORMING FEATURES</h3>
                </div>
                <div className="space-y-3">
                  {reportData.bestFeatures?.map((item, index) => (
                    <div key={index} className="flex bg-white p-3 rounded-lg shadow-sm">
                      <div className="flex-1 flex flex-col justify-center px-4">
                        <div className="flex justify-between items-center">
                          <div className="flex items-center">
                            <span className="font-medium">{item.name}</span>
                            {item.name.toLowerCase().includes('video') && (
                              <Play className="h-4 w-4 text-blue-600 ml-2" />
                            )}
                            {item.name.toLowerCase().includes('image') && (
                              <Image className="h-4 w-4 text-purple-600 ml-2" />
                            )}
                          </div>
                          <span className="text-green-600 font-bold">{formatPercent(item.impact)}</span>
                        </div>
                        <div className="mt-1 text-sm text-gray-500 flex justify-between">
                          <span>ROAS: {item.roas.toFixed(2)}x</span>
                          <span>Spend: {formatDollar(item.spend)}</span>
                        </div>
                        {renderFeatureExamples(item.name, item, true)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-red-50 p-3 sm:p-5 rounded-lg">
                <div className="flex items-center mb-4">
                  <XCircle className="h-6 w-6 text-red-600 mr-2" />
                  <h3 className="text-lg font-bold text-red-800">UNDERPERFORMING FEATURES</h3>
                </div>
                <div className="space-y-3">
                  {reportData.worstFeatures?.map((item, index) => (
                    <div key={index} className="flex bg-white p-3 rounded-lg shadow-sm">
                      <div className="flex-1 flex flex-col justify-center px-4">
                        <div className="flex justify-between items-center">
                          <div className="flex items-center">
                            <span className="font-medium">{item.name}</span>
                            {item.name.toLowerCase().includes('video') && (
                              <Play className="h-4 w-4 text-blue-600 ml-2" />
                            )}
                            {item.name.toLowerCase().includes('image') && (
                              <Image className="h-4 w-4 text-purple-600 ml-2" />
                            )}
                          </div>
                          <span className="text-red-600 font-bold">{formatPercent(item.impact)}</span>
                        </div>
                        <div className="mt-1 text-sm text-gray-500 flex justify-between">
                          <span>ROAS: {item.roas.toFixed(2)}x</span>
                          <span>Spend: {formatDollar(item.spend)}</span>
                        </div>
                        {renderFeatureExamples(item.name, item, false)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Top Performers Tab */}
        {activeTab === 'topPerformers' && (
          <div>
            <h2 className="text-xl font-bold mb-4 text-gray-800">Top Performing Ads by ROAS</h2>

            <div className="mb-4 p-4 bg-blue-50 rounded-lg">
              <p>These ads represent the highest ROAS values in your dataset, ranked by performance efficiency. Only ads with at least 0.1% of total spending are included to ensure meaningful analysis.</p>
              <p className="text-sm text-blue-600 mt-2">💡 <strong>Tip:</strong> Click on any column header to sort the data. Click again to reverse the sort order.</p>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full bg-white">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="py-3 px-4 text-left">Rank</th>
                    <th className="py-3 px-4 text-left">Ad Name</th>
                    <th 
                      className="py-3 px-4 text-right cursor-pointer hover:bg-gray-200 transition-colors"
                      onClick={() => handleTopPerformersSort('roas')}
                    >
                      ROAS
                      {topPerformersSortConfig.key === 'roas' && (
                        <span className="ml-1 text-blue-600">
                          {topPerformersSortConfig.direction === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </th>
                    <th 
                      className="py-3 px-4 text-right cursor-pointer hover:bg-gray-200 transition-colors"
                      onClick={() => handleTopPerformersSort('ctr')}
                    >
                      CTR
                      {topPerformersSortConfig.key === 'ctr' && (
                        <span className="ml-1 text-blue-600">
                          {topPerformersSortConfig.direction === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </th>
                    <th 
                      className="py-3 px-4 text-right cursor-pointer hover:bg-gray-200 transition-colors"
                      onClick={() => handleTopPerformersSort('spend')}
                    >
                      Spend
                      {topPerformersSortConfig.key === 'spend' && (
                        <span className="ml-1 text-blue-600">
                          {topPerformersSortConfig.direction === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </th>
                    <th 
                      className="py-3 px-4 text-right cursor-pointer hover:bg-gray-200 transition-colors"
                      onClick={() => handleTopPerformersSort('impressions')}
                    >
                      Impressions
                      {topPerformersSortConfig.key === 'impressions' && (
                        <span className="ml-1 text-blue-600">
                          {topPerformersSortConfig.direction === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {getSortedTopPerformers().map((ad, index) => (
                    <tr key={index} className="border-b bg-green-50">
                      <td className="py-3 px-4">{index + 1}</td>
                      <td className="py-3 px-4 font-medium">
                        <div>
                          <div>{ad.name}</div>
                          {renderFeatureExamples(ad.name, ad, true)}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-right font-bold text-green-600">{ad.roas.toFixed(2)}x</td>
                      <td className="py-3 px-4 text-right">{ad.ctr.toFixed(2)}%</td>
                      <td className="py-3 px-4 text-right">{formatDollar(ad.spend)}</td>
                      <td className="py-3 px-4 text-right">{ad.impressions.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Creative Analysis Tab */}
        {activeTab === 'visual' && (
          <div>
            <h2 className="text-xl font-bold mb-4 text-gray-800">Creative Analysis</h2>

            {/* Visual Subtabs */}
            <div className="flex border-b mb-6">
              <button
                className={`py-2 px-4 font-medium ${visualSubTab === 'adTypes' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500'}`}
                onClick={() => {
                  setVisualSubTab('adTypes');
                  setSortConfig({ key: 'roas', direction: 'desc' });
                }}>
                <BarChart3 className="h-4 w-4 inline mr-1" />
                Ad Types
              </button>
              <button
                className={`py-2 px-4 font-medium ${visualSubTab === 'features' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500'}`}
                onClick={() => {
                  setVisualSubTab('features');
                  setSortConfig({ key: 'roas', direction: 'desc' });
                }}>
                <Palette className="h-4 w-4 inline mr-1" />
                Creative Features
              </button>
            </div>

            {/* Visual Elements Content */}
            <div>
              <div className="mb-4 p-4 bg-blue-50 rounded-lg">
                <p>
                  Comprehensive analysis of {visualSubTab === 'adTypes' ? 'all ad types' : 'all creative features'} and their impact on performance and ROAS. 
                  <strong> Showing up to 50 {visualSubTab === 'adTypes' ? 'ad types' : 'features'}</strong> - click column headers to sort.
                </p>
              </div>

              <div className="mb-6">
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={getVisualData().sort((a, b) => b.impact - a.impact)}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="name" 
                      tick={{ fontSize: 10 }}
                      angle={getVisualData().some(item => item.name && item.name.toString().length > 8) ? -45 : 0}
                      textAnchor={getVisualData().some(item => item.name && item.name.toString().length > 8) ? "end" : "middle"}
                      height={getVisualData().some(item => item.name && item.name.toString().length > 8) ? 80 : 60}
                      interval={0}
                    />
                    <YAxis label={{ value: 'ROAS Impact (%)', angle: -90, position: 'insideLeft', fontSize: 10 }} tick={{ fontSize: 10 }} />
                    <Tooltip
                      formatter={(value, name) => [formatPercent(value), "ROAS Impact"]}
                    />
                    <Bar dataKey="impact" fill={(data) => getColorForImpact(data.impact)} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="mb-4">
                <p className="text-sm text-gray-600">
                  Displaying <strong>{getVisualData().length}</strong> {visualSubTab === 'adTypes' ? 'ad types' : 'creative features'}
                  {sortConfig.key && (
                    <span className="ml-2 text-blue-600">
                      (sorted by {sortConfig.key} {sortConfig.direction === 'asc' ? '↑' : '↓'})
                    </span>
                  )}
                </p>
              </div>
              
              <div className="overflow-x-auto mb-6">
                <table className="min-w-full bg-white">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="py-3 px-4 text-left">
                        <button 
                          onClick={() => handleSort('name')}
                          className="flex items-center hover:text-blue-600 transition-colors"
                        >
                          {visualSubTab === 'adTypes' ? 'Ad Type' : 'Feature'}
                          {sortConfig.key === 'name' && (
                            <span className="ml-1">
                              {sortConfig.direction === 'asc' ? '↑' : '↓'}
                            </span>
                          )}
                        </button>
                      </th>
                      <th className="py-3 px-4 text-right">
                        <button 
                          onClick={() => handleSort('impact')}
                          className="flex items-center justify-end w-full hover:text-blue-600 transition-colors"
                        >
                          ROAS Impact
                          {sortConfig.key === 'impact' && (
                            <span className="ml-1">
                              {sortConfig.direction === 'asc' ? '↑' : '↓'}
                            </span>
                          )}
                        </button>
                      </th>
                      <th className="py-3 px-4 text-right">
                        <button 
                          onClick={() => handleSort('roas')}
                          className="flex items-center justify-end w-full hover:text-blue-600 transition-colors"
                        >
                          ROAS Value
                          {sortConfig.key === 'roas' && (
                            <span className="ml-1">
                              {sortConfig.direction === 'asc' ? '↑' : '↓'}
                            </span>
                          )}
                        </button>
                      </th>
                      <th className="py-3 px-4 text-right">
                        <button 
                          onClick={() => handleSort('spend')}
                          className="flex items-center justify-end w-full hover:text-blue-600 transition-colors"
                        >
                          Spend
                          {sortConfig.key === 'spend' && (
                            <span className="ml-1">
                              {sortConfig.direction === 'asc' ? '↑' : '↓'}
                            </span>
                          )}
                        </button>
                      </th>
                      <th className="py-3 px-4 text-right">
                        <button 
                          onClick={() => handleSort('count')}
                          className="flex items-center justify-end w-full hover:text-blue-600 transition-colors"
                        >
                          Count
                          {sortConfig.key === 'count' && (
                            <span className="ml-1">
                              {sortConfig.direction === 'asc' ? '↑' : '↓'}
                            </span>
                          )}
                        </button>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {getVisualData().map((item, index) => (
                      <tr key={index} className={`border-b ${item.impact > 0 ? 'bg-green-50' : 'bg-red-50'}`}>
                        <td className="py-3 px-4 font-medium">
                          <div>
                            <div>{item.name}</div>
                            {renderFeatureExamples(item.name, item, item.impact > 0)}
                          </div>
                        </td>
                        <td className={`py-3 px-4 text-right font-bold ${item.impact > 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {formatPercent(item.impact)}
                        </td>
                        <td className="py-3 px-4 text-right">{item.roas.toFixed(2)}x</td>
                        <td className="py-3 px-4 text-right">{formatDollar(item.spend)}</td>
                        <td className="py-3 px-4 text-right">{item.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Creative Insights Tab */}
        {activeTab === 'insights' && (
          <div>
            <h2 className="text-xl font-bold mb-6 text-gray-800 flex items-center">
              <Sparkles className="h-6 w-6 text-purple-500 mr-3" />
              AI-Powered Creative Insights
            </h2>
            
            {!aiInsights.keyActionItems && !aiInsights.topPerformerInsights && !aiInsights.keyRecommendations && (
              <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-yellow-800 text-sm">
                  <strong>Note:</strong> This report was shared before AI insights were available. 
                  New reports now automatically include AI-powered insights and recommendations.
                </p>
              </div>
            )}

            <div className="space-y-8">
              {/* Key Action Items */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg border border-blue-200">
                <h3 className="text-lg font-semibold mb-4 flex items-center text-blue-800">
                  <CheckCircle className="h-5 w-5 text-blue-600 mr-2" />
                  Key Action Items
                </h3>
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <div className="whitespace-pre-line text-gray-700 leading-relaxed">
                    {aiInsights.keyActionItems || 'This report was shared before AI insights were available. AI insights are now generated automatically when reports are created.'}
                  </div>
                </div>
              </div>

              {/* Top Performer Insights */}
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-lg border border-green-200">
                <h3 className="text-lg font-semibold mb-4 flex items-center text-green-800">
                  <BarChart3 className="h-5 w-5 text-green-600 mr-2" />
                  Top Performer Analysis
                </h3>
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <div className="whitespace-pre-line text-gray-700 leading-relaxed">
                    {aiInsights.topPerformerInsights || 'This report was shared before AI insights were available. AI insights are now generated automatically when reports are created.'}
                  </div>
                </div>
              </div>

              {/* Strategic Recommendations */}
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 rounded-lg border border-purple-200">
                <h3 className="text-lg font-semibold mb-4 flex items-center text-purple-800">
                  <Sparkles className="h-5 w-5 text-purple-600 mr-2" />
                  Strategic Recommendations
                </h3>
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <div className="whitespace-pre-line text-gray-700 leading-relaxed">
                    {aiInsights.keyRecommendations || 'This report was shared before AI insights were available. AI insights are now generated automatically when reports are created.'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Fullscreen Media Modal */}
      {fullscreenMedia && (
        <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4">
          <div className="relative w-full h-full flex items-center justify-center overflow-auto">
            <div className="relative max-w-[90vw] max-h-[90vh] flex items-center justify-center">
              {fullscreenMedia.type === 'video' ? (
                <video
                  src={fullscreenMedia.url}
                  controls
                  autoPlay
                  className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
                  style={{ maxWidth: '90vw', maxHeight: '90vh' }}
                  onError={(e) => {
                    console.error('Video failed to load in modal:', fullscreenMedia.url);
                    e.target.style.display = 'none';
                  }}
                />
              ) : (
                <img
                  src={fullscreenMedia.url}
                  alt={fullscreenMedia.title}
                  className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
                  style={{ maxWidth: '90vw', maxHeight: '90vh' }}
                  onError={(e) => {
                    console.error('Image failed to load in modal:', fullscreenMedia.url);
                    e.target.style.display = 'none';
                  }}
                />
              )}
              <button
                onClick={() => setFullscreenMedia(null)}
                className="absolute top-4 right-4 p-3 bg-black bg-opacity-70 text-white rounded-full hover:bg-opacity-90 transition-all z-10"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notifications */}
      <Toaster 
        position="top-right"
        toastOptions={{
          duration: 3000,
          style: {
            background: '#1e293b',
            color: '#f8fafc',
            border: '1px solid #475569',
          },
          success: {
            duration: 3000,
            iconTheme: {
              primary: '#10b981',
              secondary: '#f8fafc',
            },
          },
          error: {
            duration: 4000,
            iconTheme: {
              primary: '#ef4444',
              secondary: '#f8fafc',
            },
          },
        }}
      />
    </div>
  );
};

export default SharedCreativeReport;
